import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  HiOutlineUserPlus,
  HiOutlineMusicalNote,
  HiOutlineTv,
} from "react-icons/hi2";
import AddLeaderModal from "./AddLeaderModal";
import AddMemberModal from "./AddMemberModal";
import InviteToEventModal from "./InviteToEventModal";
import SearchAddSongModal from "../Ministry/SearchAddSongModal";
import SongDetailModal from "../Songs/SongDetailModal";
import EventHeader from "./Components/EventHeader";
import PeopleList from "./Components/PeopleList";
import SongList from "./Components/SongList";
import CommentSection from "./Components/CommentSection";
import DropdownMenu from "./Components/DropdownMenu";
import PresentationMode from "./Components/PresentationMode";
import ServiceEventDetail from "../Components/ServiceEventDetail";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { Toast } from "../../../components/Toast";
import { doc, updateDoc } from "firebase/firestore";
import { fetchEvent } from "./utils/fetchFunctions";
import { handleRemoveSong, handleSetPlaylist } from "./utils/handleFunctions";
import { db } from "../../../db/firebase";
import { useAuth } from "../../../context/AuthContext";

const EventDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { organizationId } = useAuth();
  const [event, setEvent] = useState<any | null>(null);
  const [leaders, setLeaders] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [songs, setSongs] = useState<any[]>([]);
  const [leaderColors, setLeaderColors] = useState<Record<string, string>>({});
  const [memberColors, setMemberColors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [settingPlaylist, setSettingPlaylist] = useState(false);
  const [isCurrentPlaylist, setIsCurrentPlaylist] = useState(false);
  const [showLeaderModal, setShowLeaderModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAddSongModal, setShowAddSongModal] = useState(false);
  const [showPresentationMode, setShowPresentationMode] = useState(false);
  const [selectedSong, setSelectedSong] = useState<any | null>(null);
  const [songToRemove, setSongToRemove] = useState<any | null>(null);
  const [showRemovedToast, setShowRemovedToast] = useState(false);
  const [removingSong, setRemovingSong] = useState(false);

  useEffect(() => {
    if (!organizationId) return;
    const unsubscribe = fetchEvent(
      id,
      organizationId,
      setEvent,
      setLeaders,
      setLeaderColors,
      setMembers,
      setMemberColors,
      setSongs,
      setIsCurrentPlaylist,
      setLoading,
    );
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [id, organizationId]);

  const handleAddLeader = () => setShowLeaderModal(true);
  const handleAddMember = () => setShowMemberModal(true);
  const handleModalClose = () => {
    setShowLeaderModal(false);
    setShowMemberModal(false);
    setShowInviteModal(false);
    setShowAddSongModal(false);
    setShowPresentationMode(false);
    setSelectedSong(null);
  };

  const handleSearchAddSongs = () => setShowAddSongModal(true);

  const handleRequestRemoveSong = (song: any) => {
    setSongToRemove(song);
  };

  const handleConfirmRemoveSong = async () => {
    if (!songToRemove || !id) return;
    setRemovingSong(true);
    try {
      await handleRemoveSong(songToRemove.id, id, setSongs);
      setSongToRemove(null);
      setShowRemovedToast(true);
    } catch (err) {
      console.error("Error removing song:", err);
    } finally {
      setRemovingSong(false);
    }
  };

  const handleMoveSong = (dragIndex: number, hoverIndex: number) => {
    const updatedSongs = [...songs];
    const [draggedSong] = updatedSongs.splice(dragIndex, 1);
    updatedSongs.splice(hoverIndex, 0, draggedSong);
    setSongs(updatedSongs);

    const eventDocRef = doc(db, "events", id!);
    updateDoc(eventDocRef, {
      songs: updatedSongs.map((song) => song.id),
    });
  };

  const setPlaylist = () =>
    handleSetPlaylist(
      organizationId,
      songs,
      id!,
      setSettingPlaylist,
      setIsCurrentPlaylist,
    );

  return (
    <div className="-m-4 md:m-0">
      {loading ? (
        <SkeletonWrapper />
      ) : event ? (
        event.type === "Service" ? (
          <ServiceEventDetail
            event={event}
            eventId={id!}
            organizationId={organizationId!}
          />
        ) : (
          <div className="space-y-5 sm:space-y-6">
            <div className="rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex flex-col gap-5 min-w-0">
                <EventHeader
                  title={event.title}
                  description={event.description}
                  date={event.date}
                  time={event.time}
                  type={event.type}
                />
                <div className="flex flex-wrap items-center gap-2 w-full min-w-0">
                  {event.type === "Worship" && songs.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowPresentationMode(true)}
                      className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <HiOutlineTv className="w-4 h-4" />
                      {t("presentationMode") || "Present"}
                    </button>
                  )}
                  {event.type === "Worship" && (
                    <button
                      type="button"
                      onClick={handleSearchAddSongs}
                      className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-sm font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] transition-colors"
                    >
                      <HiOutlineMusicalNote className="w-4 h-4" />
                      {t("addSongs") || t("searchAddSongs") || "Add songs"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(true)}
                    className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <HiOutlineUserPlus className="w-4 h-4" />
                    {t("invite") || t("inviteToEvent")}
                  </button>
                  <DropdownMenu
                    onSetCurrentPlaylist={setPlaylist}
                    onInviteToEvent={() => setShowInviteModal(true)}
                    onSearchAddSongs={handleSearchAddSongs}
                    isCurrentPlaylist={isCurrentPlaylist}
                  />
                </div>
              </div>
              {isCurrentPlaylist && (
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-800">
                  {t("currentPlaylist")}
                </div>
              )}
            </div>

            <div
              className={`grid grid-cols-1 gap-5 sm:gap-6 ${
                event.type === "Worship" ? "xl:grid-cols-3" : ""
              }`}
            >
              <div
                className={`grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 min-w-0 ${
                  event.type === "Worship" ? "xl:col-span-1 xl:grid-cols-1" : ""
                }`}
              >
                <PeopleList
                  title={t("voces")}
                  people={leaders}
                  colors={leaderColors}
                  onAdd={handleAddLeader}
                />
                <PeopleList
                  title={t("musicians")}
                  people={members}
                  colors={memberColors}
                  onAdd={handleAddMember}
                />
              </div>
              {event.type === "Worship" && (
                <div className="xl:col-span-2 min-w-0">
                  <SongList
                    songs={songs}
                    handleMoveSong={handleMoveSong}
                    handleRemoveSong={handleRequestRemoveSong}
                    settingPlaylist={settingPlaylist}
                    isCurrentPlaylist={isCurrentPlaylist}
                    handleSetPlaylist={setPlaylist}
                    handleAddSong={handleSearchAddSongs}
                    onPresentationMode={() => setShowPresentationMode(true)}
                    onViewSong={(song) => setSelectedSong(song)}
                  />
                </div>
              )}
            </div>

            <CommentSection
              eventId={id!}
              initialComments={event?.comments || []}
            />
          </div>
        )
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-10 text-center">
          <p className="text-gray-600 dark:text-gray-300">
            {t("eventNotExistOrNoPermission")}
          </p>
          <Link
            to="/events"
            className="inline-flex mt-4 text-sm font-medium text-[#5932EA] hover:underline"
          >
            {t("backToEvents") || "Back to events"}
          </Link>
        </div>
      )}

      {showLeaderModal && event && (
        <AddLeaderModal
          eventId={id!}
          organizationId={event.organizationId}
          onClose={handleModalClose}
        />
      )}
      {showMemberModal && event && (
        <AddMemberModal
          eventId={id!}
          organizationId={event.organizationId}
          onClose={handleModalClose}
        />
      )}
      {showInviteModal && event && (
        <InviteToEventModal
          eventId={id!}
          organizationId={event.organizationId}
          leaders={leaders.map((leader) => leader.id)}
          members={members.map((member) => member.id)}
          onClose={handleModalClose}
        />
      )}
      {showAddSongModal && (
        <SearchAddSongModal
          eventId={id!}
          currentSongs={songs}
          onClose={() => setShowAddSongModal(false)}
        />
      )}
      {selectedSong && (
        <SongDetailModal
          song={selectedSong}
          onClose={() => setSelectedSong(null)}
          organizationId={organizationId}
        />
      )}
      {showPresentationMode && (
        <PresentationMode
          songIds={songs.map((s) => s.id)}
          eventTitle={event?.title}
          organizationId={organizationId}
          onClose={() => setShowPresentationMode(false)}
        />
      )}
      <ConfirmationModal
        isOpen={!!songToRemove}
        onClose={() => setSongToRemove(null)}
        onConfirm={handleConfirmRemoveSong}
        message={t("confirmRemoveSongFromEvent")}
        loading={removingSong}
      />
      <Toast
        message={t("songRemovedFromEvent")}
        isVisible={showRemovedToast}
        onDismiss={() => setShowRemovedToast(false)}
      />
    </div>
  );
};

const SkeletonWrapper: React.FC = () => (
  <div className="space-y-5 sm:space-y-6 animate-pulse">
    <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700" />
    <div className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4">
      <div className="flex gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gray-200 dark:bg-gray-700 shrink-0" />
        <div className="flex-1 space-y-3">
          <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-8 w-2/3 max-w-md rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-48 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="space-y-5">
        <div className="h-40 rounded-2xl bg-gray-200 dark:bg-gray-700" />
        <div className="h-40 rounded-2xl bg-gray-200 dark:bg-gray-700" />
      </div>
      <div className="lg:col-span-2 h-72 rounded-2xl bg-gray-200 dark:bg-gray-700" />
    </div>
    <div className="h-48 rounded-2xl bg-gray-200 dark:bg-gray-700" />
  </div>
);

export default EventDetail;
